import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { oltAPI, technicalAPI, acsAPI, fiberAPI } from '../../services/api';
import { LoadingBlock, EmptyState } from '../../components/technical/TechnicalUI';
import { DeviceDetailModal } from '../AcsPage';
import { ArrowLeft, Wifi, Cpu, User, Radio, Activity, ExternalLink, Settings, GitBranch } from 'lucide-react';
import useDarkReady from '../../theme/useDarkReady';

function Field({ label, value }) {
  return (
    <div style={{ padding: '9px 0', borderBottom: '1px solid var(--cb-f1f5f9)', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: '.85rem', fontWeight: 600, textAlign: 'right' }}>{value ?? '—'}</span>
    </div>
  );
}

// Customer Detail deliberately last — Hardware/TR069/Fiber/Bandwidth are what a technician
// standing at the ONU reaches for first; the customer's own record is the one tab that
// isn't about the equipment in front of them.
const TABS = [
  { key: 'hardware',  label: 'Hardware', icon: Cpu },
  { key: 'tr069',     label: 'TR069', icon: Radio },
  { key: 'fiber',     label: 'Fiber Mapping', icon: GitBranch },
  { key: 'bandwidth', label: 'Bandwidth', icon: Activity },
  { key: 'customer',  label: 'Customer Detail', icon: User },
];

// Live rx/tx sampled client-side into a short scrolling buffer — there's no server-side
// per-customer history (only the NAS uplink graph keeps one), so this behaves like a
// stopwatch: it starts empty each time the page opens and fills in while you're looking at
// it, same "anchored to now" feel as the Dashboard's NAS uplink bandwidth panel. Polled once here
// (not separately per tab) so the always-visible strip under the title and the full
// Bandwidth tab graph below share one buffer instead of racing two independent pollers.
const BW_POLL_MS = 3000;
const BW_MAX_PTS = 40;

function fmtRate(v) {
  if (v == null) return '0';
  if (v >= 100) return v.toFixed(0);
  if (v >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

// Same three zones as the Power Thresholds bar on Settings > OLT (below goodMin = "Weak /
// Critical", within [goodMin, goodMax] = "Acceptable range", above goodMax = "Saturated") —
// reads the admin-configured bands instead of a hardcoded -27/-24 cutoff, so moving the
// sliders in Settings actually changes what a technician sees as red/amber/green here.
function rxColor(val, t) {
  if (val == null || !t) return 'var(--c-111827)';
  if (val >= t.goodRxMin && val <= t.goodRxMax) return 'var(--c-065f46)';
  if (val < t.goodRxMin) return 'var(--c-991b1b)';
  return 'var(--c-92400e)';
}

function usePowerThresholds() {
  const [thresholds, setThresholds] = useState(null);
  useEffect(() => {
    technicalAPI.getPowerThresholds().then(({ data }) => setThresholds(data.data)).catch(() => {});
  }, []);
  return thresholds;
}

function useLiveFlowHistory(sessionKey) {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setHistory([]);
    if (!sessionKey) return;
    let mounted = true;
    const tick = () => {
      technicalAPI.getLiveRates().then(({ data }) => {
        if (!mounted) return;
        const v = data.data?.[sessionKey];
        const rx = v ? v[0] : 0;
        const tx = v ? v[1] : 0;
        const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
        setHistory(h => [...h.slice(-(BW_MAX_PTS - 1)), { time, rx, tx }]);
      }).catch(() => {});
    };
    tick();
    const t = setInterval(tick, BW_POLL_MS);
    return () => { mounted = false; clearInterval(t); };
  }, [sessionKey]);

  return history;
}

// Compact, always-visible graph under the ONU label — no axis labels/tooltip (that's what
// the full Bandwidth tab is for), just the flowing shape plus the current numbers.
function LiveFlowStrip({ customer, history }) {
  if (!customer?.live) return null;
  const last = history[history.length - 1];

  return (
    <div style={{ margin: '4px 0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 4 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', color: 'var(--c-6b7280)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--cb-0ca30c)', display: 'inline-block' }} />
          LIVE
        </span>
        <span style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-1e40af)' }}>↓{fmtRate(last?.rx)} <span style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text-muted)' }}>Mbps</span></span>
        <span style={{ fontSize: '.95rem', fontWeight: 800, color: 'var(--c-ea580c)' }}>↑{fmtRate(last?.tx)} <span style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text-muted)' }}>Mbps</span></span>
      </div>
      <ResponsiveContainer width="100%" height={54}>
        <AreaChart data={history} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="onuLiveRxG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--cl-1e40af)" stopOpacity={0.25} /><stop offset="95%" stopColor="var(--cl-1e40af)" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="onuLiveTxG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--cl-ea580c)" stopOpacity={0.25} /><stop offset="95%" stopColor="var(--cl-ea580c)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="rx" stroke="var(--cl-1e40af)" strokeWidth={1.5} fill="url(#onuLiveRxG)" dot={false} isAnimationActive={false} connectNulls={false} />
          <Area type="monotone" dataKey="tx" stroke="var(--cl-ea580c)" strokeWidth={1.5} fill="url(#onuLiveTxG)" dot={false} isAnimationActive={false} connectNulls={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function BandwidthTab({ customer, history }) {
  if (!customer) return <EmptyState icon={Activity} text="No customer linked to this ONU." />;
  if (!customer.live) return <EmptyState icon={Activity} text="No active PPPoE session — nothing is flowing right now." />;

  const last = history[history.length - 1];

  return (
    <div>
      <div style={{ display: 'flex', gap: 20, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-1e40af)', textTransform: 'uppercase', letterSpacing: '.04em' }}>↓ Download</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--c-1e40af)' }}>{fmtRate(last?.rx)} <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Mbps</span></div>
        </div>
        <div>
          <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-ea580c)', textTransform: 'uppercase', letterSpacing: '.04em' }}>↑ Upload</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--c-ea580c)' }}>{fmtRate(last?.tx)} <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Mbps</span></div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={history} margin={{ top: 4, right: 14, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="onuRxG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--cl-1e40af)" stopOpacity={0.2} /><stop offset="95%" stopColor="var(--cl-1e40af)" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="onuTxG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--cl-ea580c)" stopOpacity={0.2} /><stop offset="95%" stopColor="var(--cl-ea580c)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--cl-f1f5f9)" />
          <XAxis dataKey="time" tick={{ fontSize: 10 }} minTickGap={40} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}M`} width={40} domain={[0, 'auto']} />
          <Tooltip contentStyle={{ fontSize: '.78rem', borderRadius: 6 }} formatter={(v, n) => [`${v} Mbps`, n === 'rx' ? '↓ Download' : '↑ Upload']} />
          <Area type="monotone" dataKey="rx" stroke="var(--cl-1e40af)" strokeWidth={2} fill="url(#onuRxG)" dot={false} isAnimationActive={false} connectNulls={false} />
          <Area type="monotone" dataKey="tx" stroke="var(--cl-ea580c)" strokeWidth={2} fill="url(#onuTxG)" dot={false} isAnimationActive={false} connectNulls={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function CustomerDetailTab({ customer }) {
  if (!customer) return <EmptyState icon={User} text="No customer linked to this ONU." />;
  const hasBroadband = customer.customerType === 'broadband' || customer.customerType === 'both';
  const hasCatv = customer.customerType === 'catv' || customer.customerType === 'both';
  return (
    <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 10, padding: '4px 18px', background: 'var(--cb-ffffff)' }}>
      <Field label="Name" value={customer.name} />
      <Field label="Area" value={customer.area} />
      <Field label="Address" value={customer.line1} />
      {hasBroadband && <>
        <Field label="PPPoE Username" value={customer.username} />
        <Field label="Plan" value={customer.plan ? `${customer.plan.name} (${customer.plan.download}/${customer.plan.upload} ${customer.plan.unit || 'Mbps'})` : 'Not assigned'} />
        <Field label="Session" value={customer.sessionOnline == null ? '—' : customer.sessionOnline ? 'Active' : 'None'} />
        <Field label="Broadband Expiry" value={customer.broadbandExpiry ? new Date(customer.broadbandExpiry).toLocaleDateString() : '—'} />
      </>}
      {hasCatv && <>
        <Field label="CATV Account Number" value={customer.catvAccountNumber} />
        <Field label="CATV Expiry" value={customer.catvExpiry ? new Date(customer.catvExpiry).toLocaleDateString() : '—'} />
      </>}
    </div>
  );
}

// Primarily keyed off the ONU's own hardware (GPON) serial from the Hardware tab's data —
// but that serial isn't always what the same physical router reports via its own TR-069
// Inform (seen on some ZTE units, which register with the ACS under a completely different
// SerialNumber). So if the direct lookup 404s, fall back to resolving the ACS device through
// the linked customer instead — same two-step CustomerDetailPage's own TR069 tab already
// uses (`getDeviceByCustomer` then `getDevice`), just done here without a page navigation.
function Tr069Tab({ serial, customer }) {
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noConfig, setNoConfig] = useState(false);
  const [resolvedSerial, setResolvedSerial] = useState(null);
  const [showManage, setShowManage] = useState(false);

  const load = useCallback(() => {
    if (!serial) { setNoConfig(true); return; }
    setLoading(true);
    setNoConfig(false);

    acsAPI.getDevice(serial)
      .then(({ data }) => { setDevice(data.data); setResolvedSerial(serial); })
      .catch(() => {
        if (!customer?._id) throw new Error('no customer to fall back to');
        return acsAPI.getDeviceByCustomer(customer._id)
          .then(({ data }) => acsAPI.getDevice(data.data.serial))
          .then(({ data }) => { setDevice(data.data); setResolvedSerial(data.data.serial); });
      })
      .catch(() => setNoConfig(true))
      .finally(() => setLoading(false));
  }, [serial, customer]);

  useEffect(() => { setDevice(null); load(); }, [load]);

  if (loading) return <LoadingBlock />;
  if (noConfig || !device) return <EmptyState icon={Radio} text="No configuration of TR069 done for this customer." />;

  return (
    <div>
      <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 10, padding: '4px 18px', background: 'var(--cb-ffffff)', marginBottom: 14 }}>
        <Field label="Model" value={device.model || device.productClass} />
        <Field label="Manufacturer" value={device.manufacturer} />
        <Field label="Firmware" value={device.swVersion} />
        <Field label="Status" value={device.online ? 'Online' : 'Offline'} />
        <Field label="Uptime" value={device.upTime ? `${Math.floor(device.upTime / 3600)}h` : '—'} />
        <Field label="WAN IP" value={device.ip || device.remoteIp} />
        {(device.wifiFull || []).map((w, i) => (
          <Field key={i} label={`WiFi ${w.band}GHz`} value={w.ssid || '—'} />
        ))}
      </div>
      {/* Same DeviceDetailModal the full ACS page uses (Refresh, WiFi, WAN, Reboot,
          Factory Reset, MAC blocking, Speed Test, relink/unlink, raw params) — opened
          inline here so a technician never has to leave this ONU's page to act on it. */}
      <button
        onClick={() => setShowManage(true)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          width: '100%', minHeight: 44, borderRadius: 10, border: '1px solid var(--primary)',
          background: 'var(--cb-ffffff)', color: 'var(--primary)', fontSize: '.88rem', fontWeight: 600, cursor: 'pointer',
        }}
      >
        <Settings size={15} /> Manage / Edit TR-069 Settings
      </button>
      {showManage && (
        <DeviceDetailModal
          serial={resolvedSerial || serial}
          onClose={() => setShowManage(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// Was previously a static placeholder that always showed the same "not mapped" copy
// regardless of whether ONUMap actually had a row for this serial — this now fetches the
// real mapping (GET /api/v1/fiber/mappings/:onuSerial) so a genuinely mapped ONU shows its
// splitter/port instead of looking broken.
function FiberMappingTab({ serial, navigate }) {
  const [mapping, setMapping] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notMapped, setNotMapped] = useState(false);

  useEffect(() => {
    if (!serial) { setNotMapped(true); return; }
    setLoading(true);
    setNotMapped(false);
    setMapping(null);
    fiberAPI.getMapping(serial)
      .then(({ data }) => { if (data.data) setMapping(data.data); else setNotMapped(true); })
      .catch(() => setNotMapped(true))
      .finally(() => setLoading(false));
  }, [serial]);

  if (loading) return <LoadingBlock />;

  return (
    <div>
      {notMapped || !mapping ? (
        <EmptyState icon={GitBranch} text="This ONU isn't mapped on the Fiber Map yet." />
      ) : (
        <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 10, padding: '4px 18px', background: 'var(--cb-ffffff)', marginBottom: 14 }}>
          <Field label="Splitter" value={mapping.splitterId?.name} />
          <Field label="Splitter Ratio" value={mapping.splitterId?.ratio} />
          <Field label="Splitter Location" value={mapping.splitterId?.location} />
          <Field label="Splitter Port" value={mapping.splitterPort} />
          <Field label="PON Port" value={mapping.ponPort != null ? `${mapping.ponSlot ?? 0}/${mapping.ponPort}` : null} />
          <Field label="Notes" value={mapping.notes} />
        </div>
      )}
      <button
        onClick={() => navigate('/technical/fiber-map')}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          width: '100%', minHeight: 44, borderRadius: 10, border: '1px solid var(--primary)',
          background: 'var(--cb-ffffff)', color: 'var(--primary)', fontSize: '.88rem', fontWeight: 600, cursor: 'pointer',
        }}
      >
        <ExternalLink size={15} /> Open Fiber Map
      </button>
    </div>
  );
}

export default function TechnicalOnuDetailPage() {
  useDarkReady();
  const { slot, port, onuId } = useParams();
  const navigate = useNavigate();
  const [onu, setOnu] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('hardware');
  const history = useLiveFlowHistory(customer?.sessionKey);
  const thresholds = usePowerThresholds();

  const load = useCallback(() => {
    setLoading(true);
    // Customer lookup depends on the ONU's serial (ONUMap is keyed by serial, not reliably by
    // onuId — see the backend route's comment), so it runs after the hardware fetch resolves
    // rather than in parallel with it.
    oltAPI.getONUDetail(slot, port, onuId).then(({ data }) => data.data).catch(() => null).then(onuData => {
      setOnu(onuData);
      if (!onuData?.serialNumber) { setCustomer(null); return; }
      // Straight off ONUMap, not the (broadband-only) connections list — a catv customer or
      // a 'both' customer sharing this premise's ONU has to match here too.
      return technicalAPI.getOnuCustomer(onuData.serialNumber).then(({ data }) => setCustomer(data.data)).catch(() => setCustomer(null));
    }).finally(() => setLoading(false));
  }, [slot, port, onuId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingBlock />;
  if (!onu) return <EmptyState icon={Wifi} text="ONU not found" />;

  return (
    <div>
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '.85rem', cursor: 'pointer', padding: 0, marginBottom: 14 }}>
        <ArrowLeft size={16} /> Back
      </button>

      <div className="page-header">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {onu.label || onu.description || onu.id}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.7rem', fontWeight: 700,
            color: onu.status === 'online' ? 'var(--c-0ca30c)' : 'var(--c-d03b3b)',
            background: onu.status === 'online' ? '#0ca30c17' : '#d03b3b17',
            borderRadius: 999, padding: '3px 9px',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', background: onu.status === 'online' ? 'var(--cb-0ca30c)' : 'var(--cb-d03b3b)' }} />
            {onu.status === 'online' ? 'Online' : 'Offline'}
          </span>
        </h2>
      </div>

      <LiveFlowStrip customer={customer} history={history} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="stat-card" style={{ flex: '1 1 0', minWidth: 150 }}>
          <div className="stat-info">
            <p>RX Power</p>
            <h3 style={{ color: rxColor(onu.rxPower, thresholds) }}>{onu.rxPower != null ? `${onu.rxPower} dBm` : '—'}</h3>
          </div>
        </div>
        <div className="stat-card" style={{ flex: '1 1 0', minWidth: 150 }}>
          <div className="stat-info">
            <p>TX Power</p>
            <h3>{onu.txPower != null ? `${onu.txPower} dBm` : '—'}</h3>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 13px', borderRadius: 8, border: '1px solid',
              borderColor: tab === t.key ? 'var(--primary)' : 'var(--cb-e5e7eb)',
              background: tab === t.key ? 'var(--primary)' : 'var(--cb-ffffff)',
              color: tab === t.key ? 'var(--c-ffffff)' : 'var(--c-374151)',
              fontSize: '.82rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'hardware' && (
        <div style={{ border: '1px solid var(--cb-e5e7eb)', borderRadius: 10, padding: '4px 18px', background: 'var(--cb-ffffff)' }}>
          <Field label="PON Port" value={`${onu.slot}/${onu.port}`} />
          <Field label="ONU ID" value={onu.onuId} />
          <Field label="Serial Number" value={onu.serialNumber} />
          <Field label="Model" value={onu.model} />
          <Field label="Profile" value={onu.profile} />
          <Field label="Description" value={onu.description} />
          <Field label="Admin State" value={onu.adminState} />
          <Field label="Uptime" value={onu.aliveTime} />
          <Field label="Distance" value={onu.distance != null ? `${onu.distance} m` : null} />
          <Field label="Last Deregister" value={onu.lastDeregister} />
          <Field label="Deregister Reason" value={onu.deregisterReason} />
          {customer?.username && <>
            <Field label="Framed IPv4" value={customer.address} />
            <Field label="NAS" value={customer.nasName} />
          </>}
        </div>
      )}
      {tab === 'tr069' && <Tr069Tab serial={onu.serialNumber} customer={customer} />}
      {tab === 'fiber' && <FiberMappingTab serial={onu.serialNumber} navigate={navigate} />}
      {tab === 'bandwidth' && <BandwidthTab customer={customer} history={history} />}
      {tab === 'customer' && <CustomerDetailTab customer={customer} />}
    </div>
  );
}
